import { EventEmitter, Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, IHttpConnectionOptions } from '@microsoft/signalr';
import * as signalR from '@microsoft/signalr';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { SignalRConnectionInfo } from '@models/signalrConnectionInfo.model';
import { LoggingService } from './logging.service';
import { UserNotification } from '@models/userNotification.model';
import { Store } from '@ngrx/store';
import { updateProgress } from '../mystudies/analysis/internetreview/scraper-progress/scraper-progress.actions';
import { ScraperProgress } from '../mystudies/analysis/internetreview/scraper-progress/scraper-progress.reducer';

@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  hubConnection: HubConnection;
  messageReceived = new EventEmitter<string>();
  addNotificationReceived = new EventEmitter<UserNotification>();
  updateNotificationReceived = new EventEmitter<UserNotification>();

  private appUrl = '/api/SignalRHub/';
  private hubText = 'Azure SignalR Service Hub';

  constructor(
    private http: HttpClient,
    private logger: LoggingService,
    private store: Store<any>,
  ) {}

  private getConnectionInfo(): Observable<SignalRConnectionInfo> {
    this.logger.info(this.constructor.name, 'getConnectionInfo', 'Start');
    try {
      const requestUrl = `${this.appUrl}negotiate`;
      return this.http.post<SignalRConnectionInfo>(requestUrl, null);
    } finally {
      this.logger.info(this.constructor.name, 'getConnectionInfo', 'End');
    }
  }

  init() {
    this.logger.info(this.constructor.name, 'init', 'Start');
    try {
      this.getConnectionInfo().subscribe(
        (info: SignalRConnectionInfo) => {
          const options = {
            accessTokenFactory: () => info.accessToken,
            skipNegotiation: true,
            transport: signalR.HttpTransportType.WebSockets,
          };
          this.createConnection(info.url, options);
          this.startConnection();
        },
        (error) => {
          this.logger.error(this.constructor.name, 'init', `An error occurred during init [${error.status} ${error.statusText}]`);
          this.logger.info(this.constructor.name, 'init', `Retrying connection to ${this.hubText} ...`);
          setTimeout(() => {
            this.init();
          }, 10000);
        },
      );
    } finally {
      this.logger.info(this.constructor.name, 'init', 'End');
    }
  }

  private createConnection(url: string, options: IHttpConnectionOptions) {
    this.logger.info(this.constructor.name, 'createConnection', 'Start');
    try {
      this.hubConnection = new HubConnectionBuilder().withUrl(url, options).configureLogging(signalR.LogLevel.Debug).build();
      this.hubConnection.onclose((err) => {
        this.logger.info(this.constructor.name, 'createConnection', `${this.hubText} connection closed.`);
        this.stopHubAndunSubscribeToServerEvents();
        this.restartConnection(err);
      });
    } finally {
      this.logger.info(this.constructor.name, 'createConnection', 'End');
    }
  }

  private startConnection(): void {
    this.logger.info(this.constructor.name, 'startConnection', 'Start');
    try {
      this.hubConnection
        .start()
        .then(() => {
          this.logger.info(this.constructor.name, 'startConnection', `${this.hubText} connection started.`);
          this.subscribeToServerEvents();
        })
        .catch((err) => {
          this.restartConnection(err);
        });
    } finally {
      this.logger.info(this.constructor.name, 'startConnection', 'End');
    }
  }

  private restartConnection(err: Error): void {
    this.logger.info(this.constructor.name, 'restartConnection', 'Start');
    try {
      this.logger.info(this.constructor.name, 'restartConnection', `Error ${err}`);
      this.logger.info(this.constructor.name, 'restartConnection', `Retrying connection to ${this.hubText} ...`);
      setTimeout(() => {
        this.startConnection();
      }, 10000);
    } finally {
      this.logger.info(this.constructor.name, 'restartConnection', 'End');
    }
  }

  private subscribeToServerEvents(): void {
    this.logger.info(this.constructor.name, 'subscribeToServerEvents', 'Start');
    try {
      this.hubConnection.on('addNotification', (message: string, isError: boolean, url: string) => {
        this.logger.info(this.constructor.name, 'subscribeToServerEvents', `addNotification - Message:[${message}] ${isError ? '[Error]' : ''}`);
        const notification = new UserNotification();
        notification.message = message;
        notification.isErrorMessage = isError;
        notification.url = url;
        this.addNotificationReceived.emit(notification);
      });
      this.hubConnection.on('updateNotification', (key: string, message: string, isError: boolean) => {
        this.logger.info(
          this.constructor.name,
          'subscribeToServerEvents',
          `updateNotification - Key:[${key}] Message:[${message}] ${isError ? '[Error]' : ''}`,
        );
        const notification = new UserNotification();
        notification.key = key;
        notification.message = message;
        notification.isErrorMessage = isError;
        this.updateNotificationReceived.emit(notification);
      });
      this.hubConnection.on('updateScraperProgress', (scraperProgressNotification: any, comparableIds: any, debugInfo: any) => {
        const scraperProgress: ScraperProgress = {
          stepNumber: scraperProgressNotification.stepNumber,
          stepsCount: scraperProgressNotification.stepsCount,
          status: scraperProgressNotification.status,
          comparableIds: comparableIds,
          debugInfo: debugInfo,
          isDownloadAvailable: scraperProgressNotification.isDownloadAvailable,
        };

        this.store.dispatch(updateProgress({ scraperProgress: scraperProgress, studyId: scraperProgressNotification.studyId }));
      });
    } finally {
      this.logger.info(this.constructor.name, 'subscribeToServerEvents', 'End');
    }

    // // testing direct messages
    // this.hubConnection.on('broadcastMessage', (name: any, message: any) => {
    //  this.logger.info(this.constructor.name, 'subscribeToServerEvents', `broadcastMessage - name:[${name}] message:[${message}]`);
    //  this.messageReceived.emit(`MessageNotification - Function: ${name} ${message}`);
    // });
    // this.hubConnection.on('echo', (name: any, message: any) => {
    //  this.logger.info(this.constructor.name, 'subscribeToServerEvents', `echo - name:[${name}] message:[${message}]`);
    //  this.messageReceived.emit(`Direct User Notification echo: ${name} ${message}`);
    // });
  }

  private stopHubAndunSubscribeToServerEvents(): void {
    this.logger.info(this.constructor.name, 'stopHubAndunSubscribeToServerEvents', 'Start');
    try {
      this.hubConnection.off('addNotification');
      this.hubConnection.off('updateNotification');
      this.hubConnection.off('updateScraperProgress');
      // this.hubConnection.off('broadcastMessage');
      // this.hubConnection.off('echo');
      this.hubConnection
        .stop()
        .then(() => this.logger.info(this.constructor.name, 'stopHubAndunSubscribeToServerEvents', `${this.hubText} connection stopped`));
    } finally {
      this.logger.info(this.constructor.name, 'stopHubAndunSubscribeToServerEvents', 'End');
    }
  }
}
